"use client";

import { useState, useEffect, useRef } from "react";
import { dockApps } from "../../data/projects";
import spotify from "../../assets/imgs/spotify_logo.png";
import GlassSurface from "../../../v2/components/common/GlassSurface";

function DockIcon({ app, onHover, isHovered, index, hoveredIndex, onClick, isActive }) {
  // Calculate magnification based on distance from hovered icon
  let scale = 1;
  if (hoveredIndex !== null) {
    const distance = Math.abs(index - hoveredIndex);
    if (distance === 0) scale = 1.4;
    else if (distance === 1) scale = 1.15;
    else if (distance === 2) scale = 1.05;
  }

  // Finder and Spotify have active dots in the original
  const hasActiveDot = app.name === "Finder" || app.name === "Spotify" || isActive;

  return (
    <div
      className="relative flex flex-col items-center gap-0.5 transition-transform duration-150 ease-out origin-bottom cursor-pointer"
      style={{ transform: `scale(${scale})` }}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onClick(app.name)}
    >
      <div className="w-[49px] h-[49px] relative">
        <img
          src={app.img}
          alt={app.name}
          className="w-full h-full object-contain"
          draggable={false}
        />
      </div>
      {/* Active dot indicator */}
      {hasActiveDot && (
        <div
          className="w-[3px] h-[3px] rounded-full absolute -bottom-[2px] left-1/2 -translate-x-1/2"
          style={{ backgroundColor: "rgba(255,255,255,0.7)" }}
        />
      )}
      {/* Tooltip on hover */}
      {isHovered && (
        <div
          className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-[2px] rounded text-[8px] text-white pointer-events-none z-[999]"
          style={{
            backgroundColor: "rgba(0,0,0,0.4)",
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
          }}
        >
          {app.name}
        </div>
      )}
    </div>
  );
}

function SpotifyPopup({ isOpen, onClose, iconRef }) {
  const popupRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (popupRef.current && !popupRef.current.contains(event.target) &&
        iconRef?.current && !iconRef.current.contains(event.target)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, iconRef]);

  if (!isOpen) return null;

  return (
    <div
      ref={popupRef}
      className="absolute z-50"
      style={{
        bottom: '0px',
        left: '78%',
        transform: 'translateX(-50%)',
        transition: 'ease-in-out duration-500',
      }}
    >
      {/* Spotify Embed */}
      <div className="relative">
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 transition-all w-4 h-4 rotate-45" />
        <iframe
          title="my_spotify"
          data-testid="embed-iframe"
          style={{ borderRadius: "12px" }}
          src="https://open.spotify.com/embed/playlist/0OyKV6Er5dzwzRvpRZdIyN?utm_source=generator"
          width="300"
          height="252"
          allowFullScreen
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
        />
      </div>
    </div>
  );
}

export default function Dock({ setOpenWindow ,setSelectedPanel,setIsSubPanelOpen}) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [spotifyOpen, setSpotifyOpen] = useState(false);
  const [, setFinderOpen] = useState(false);
  const [activeWindows, setActiveWindows] = useState({
    finder: false,
    spotify: false
  });
  const spotifyIconRef = useRef(null);

  const handleIconClick = (appName) => {
    
    if (appName === "Finder") {
      setOpenWindow(true);
      setIsSubPanelOpen(false);
      setSelectedPanel('All projects')
    }else if(appName === "Photos"){
      console.log(appName);
      setOpenWindow(true);
      setSelectedPanel('photos')
    }else if (appName === "Spotify") {
      setSpotifyOpen(!spotifyOpen);
      setActiveWindows(prev => ({ ...prev, spotify: !spotifyOpen }));
    }
  };

  const handleCloseWindow = (windowType) => {
    if (windowType === 'finder') {
      setFinderOpen(false);
      setActiveWindows(prev => ({ ...prev, finder: false }));
    } else if (windowType === 'spotify') {
      setSpotifyOpen(false);
      setActiveWindows(prev => ({ ...prev, spotify: false }));
    }
  };

  // Split apps: main apps + separator + folder + separator + spotify + trash
  const mainApps = dockApps.slice(0, 17);

  return (
    <>
      <div className="absolute bottom-[15px] left-1/2 -translate-x-1/2 z-40">

  {/* Glass background */}
  <div className="absolute inset-0 z-0">
    <GlassSurface
      width={1050}
      height={85}
      borderRadius={25}
      backgroundOpacity={0.34}
      blur={9}
      displace={1.5}
      borderWidth={0}
      distortionScale={90}
      className="shadow-[0_30px_80px_rgba(0,0,0,.4)] bottom-6"
    />
  </div>

  {/* Dock icons */}
  <div className="relative z-10 bottom-3 flex items-end gap-1 px-2 py-1.5">

    {mainApps.map((app, i) => (
      <DockIcon
        key={app.name}
        app={app}
        index={i}
        hoveredIndex={hoveredIndex}
        isHovered={hoveredIndex === i}
        onHover={setHoveredIndex}
        onClick={handleIconClick}
        isActive={
          app.name === "Finder"
            ? activeWindows.finder
            : app.name === "Spotify"
              ? activeWindows.spotify
              : false
        }
      />
    ))}

    {/* Divider */}
    <div className="w-px h-[30px] mx-1 self-center opacity-30">
      <div className="w-full h-full bg-white/60" />
    </div>

    {/* Spotify */}
    <div
      ref={spotifyIconRef}
      className="w-[39px] h-[39px] relative cursor-pointer self-center transition-transform duration-150 ease-out"
      style={{
        transform:
          hoveredIndex === 101 ? "scale(1.35)" : "scale(1)",
      }}
      onMouseEnter={() => setHoveredIndex(101)}
      onMouseLeave={() => setHoveredIndex(null)}
      onClick={() => {
        setSpotifyOpen(!spotifyOpen);
        setActiveWindows((prev) => ({
          ...prev,
          spotify: !spotifyOpen,
        }));
      }}
    >
      <img src={spotify} alt="spotify" />

      <div className="w-[3px] h-[3px] rounded-full absolute -bottom-[6px] left-1/2 -translate-x-1/2 bg-white/70" />

      {hoveredIndex === 101 && (
        <div
          className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-[2px] rounded text-[8px] text-white pointer-events-none z-[999]"
          style={{
            backgroundColor: "rgba(0,0,0,0.4)",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
          }}
        >
          Spotify
        </div>
      )}
    </div>

    {/* Divider */}
    <div className="w-px h-[30px] mx-1 self-center opacity-30">
      <div className="w-full h-full bg-white/60" />
    </div>

    {/* Trash */}
    <div
      className="flex flex-col items-center cursor-pointer transition-transform duration-150 ease-out"
      style={{
        transform:
          hoveredIndex === 102 ? "scale(1.35)" : "scale(1)",
      }}
      onMouseEnter={() => setHoveredIndex(102)}
      onMouseLeave={() => setHoveredIndex(null)}
    >
      <div className="w-[49px] h-[49px] relative">
        <img
          src="https://framerusercontent.com/images/AbIAp0qW5rYTqOKWfx9xlOy8Pio.png?scale-down-to=512"
          alt="Trash"
          className="w-full h-full object-contain"
          draggable={false}
        />
      </div>

      {hoveredIndex === 102 && (
        <div
          className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-[2px] rounded text-[8px] text-white pointer-events-none z-[999]"
          style={{
            backgroundColor: "rgba(0,0,0,0.4)",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
          }}
        >
          Trash
        </div>
      )}
    </div>

  </div>
</div>

      {/* Spotify Popup */}
      <SpotifyPopup
        isOpen={spotifyOpen}
        onClose={() => handleCloseWindow('spotify')}
        iconRef={spotifyIconRef}
      />
    </>
  );
}